import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout, clearUserState, switchAccount, exitSwitchAccount } from '../../state/slices/user';
import { getTutorByUser, clearTutor } from '../../state/slices/tutor';
import LanguageToggle from '../languageToggle/languageToggle';
import './navbar.css';
import { CssBaseline, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemButton, ListItemText, Avatar, Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { getMyChildrenAPI } from '../../services/child';
import { getMyEmployeesAPI } from '../../services/business';
import { SwapHoriz, Close } from '@mui/icons-material';

// Custom Dropdown Component
const NavDropdown = ({ label, items, path, primaryColor }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const updateMenuPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 1, // Reduce gap to minimum
        left: rect.left,
      });
    }
  };

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    updateMenuPosition();
    setIsOpen(true);
  };

  const handleMouseLeave = (e) => {
    // Check if moving to menu
    const relatedTarget = e.relatedTarget;
    const isMovingToMenu = menuRef.current && relatedTarget && relatedTarget.nodeType === Node.ELEMENT_NODE && (
      menuRef.current.contains(relatedTarget) ||
      relatedTarget === menuRef.current
    );
    if (!isMovingToMenu) {
      scheduleClose();
    }
  };

  const handleMenuMouseEnter = () => {
    clearCloseTimeout();
  };

  const handleMenuMouseLeave = (e) => {
    // Check if moving back to button
    const relatedTarget = e.relatedTarget;
    const isMovingToButton = containerRef.current && relatedTarget && relatedTarget.nodeType === Node.ELEMENT_NODE && (
      containerRef.current.contains(relatedTarget) ||
      relatedTarget === containerRef.current
    );
    if (!isMovingToButton) {
      scheduleClose();
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      return true;
    }
    return false;
  };

  const handleMenuItemClick = (itemPath) => {
    setIsOpen(false);
    clearCloseTimeout();
    
    // Check if it's a signup link with role parameter
    if (itemPath.includes('/signup')) {
      navigate(itemPath);
      return;
    }
    
    const hashIndex = itemPath.indexOf('#');
    if (hashIndex !== -1) {
      const targetPath = itemPath.substring(0, hashIndex) || path || '/';
      const sectionId = itemPath.substring(hashIndex);
      const currentPath = location.pathname;
      
      if (currentPath !== targetPath) {
        navigate(targetPath);
        
        let attempts = 0;
        const maxAttempts = 30;
        const tryScroll = () => {
          attempts++;
          if (scrollToSection(sectionId)) {
            return;
          }
          if (attempts < maxAttempts) {
            setTimeout(tryScroll, 150);
          }
        };
        setTimeout(tryScroll, 500);
      } else {
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 50);
      }
    } else {
      navigate(itemPath);
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="nav-dropdown-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link to={path} className="nav-dropdown-button" style={{ '--primary-color': primaryColor }}>
        <span>{label}</span>
        <svg
          className={`nav-dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
        {isOpen && <span className="nav-dropdown-underline"></span>}
      </Link>

      {isOpen && (
        <div
          ref={menuRef}
          className="nav-dropdown-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            '--primary-color': primaryColor,
          }}
          onMouseEnter={handleMenuMouseEnter}
          onMouseLeave={handleMenuMouseLeave}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="nav-dropdown-item"
              onClick={() => handleMenuItemClick(item.path)}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom Nav Link Component
const NavLink = ({ to, children, primaryColor }) => {
  return (
    <Link to={to} className="nav-link" style={{ '--primary-color': primaryColor }}>
      {children}
    </Link>
  );
};

// Custom Avatar Component
const CustomAvatar = ({ children, size = 32 }) => {
  return (
    <div className="custom-avatar" style={{ width: `${size}px`, height: `${size}px` }}>
      {children}
    </div>
  );
};

// Custom Menu Component
const CustomMenu = ({ isOpen, onClose, anchorRef, children, primaryColor }) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="custom-menu"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
        '--primary-color': primaryColor,
      }}
    >
      {children}
    </div>
  );
};

// Custom Menu Item Component
const CustomMenuItem = ({ children, onClick, icon, danger = false }) => {
  return (
    <div
      className={`custom-menu-item ${danger ? 'danger' : ''}`}
      onClick={onClick}
    >
      {icon && <span className="custom-menu-item-icon">{icon}</span>}
      <span>{children}</span>
    </div>
  );
};

// Drawer Dropdown Item Component
const DrawerDropdownItem = ({ link, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLinkClick = (e) => {
    e.preventDefault();
    onClose();
    navigate(link.path);
  };

  const handleExpandClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSubItemClick = (itemPath) => {
    onClose();
    const hashIndex = itemPath.indexOf('#');
    if (hashIndex !== -1) {
      const targetPath = itemPath.substring(0, hashIndex) || link.path || '/';
      const sectionId = itemPath.substring(hashIndex);
      const currentPath = location.pathname;
      
      if (currentPath !== targetPath) {
        navigate(targetPath);
        setTimeout(() => {
          const element = document.querySelector(sectionId);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 500);
      } else {
        setTimeout(() => {
          const element = document.querySelector(sectionId);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 50);
      }
    } else {
      navigate(itemPath);
    }
  };

  return (
    <div className="drawer-dropdown-item">
      <div className="drawer-dropdown-header">
        <Link
          to={link.path}
          className="drawer-dropdown-link"
          onClick={handleLinkClick}
        >
          {link.name}
        </Link>
        <button
          className={`drawer-dropdown-expand ${isExpanded ? 'expanded' : ''}`}
          onClick={handleExpandClick}
          aria-expanded={isExpanded}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
      {isExpanded && (
        <div className="drawer-dropdown-content">
          {link.items.map((item, index) => (
            <button
              key={index}
              className="drawer-dropdown-subitem"
              onClick={() => handleSubItemClick(item.path)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom Drawer Component
const CustomDrawer = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {isOpen && <div className="drawer-backdrop" onClick={onClose} />}
      <div className={`custom-drawer ${isOpen ? 'open' : ''}`}>
        {children}
      </div>
    </>
  );
};

export default function NavBar() {
  const userState = useSelector(state => state.user);
  const originalUser = userState?.user;
  const switchedAccount = userState?.switchedAccount;
  const effectiveUser = switchedAccount ? switchedAccount.studentData?.userId : originalUser;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mainLinks, setMainLinks] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const profileButtonRef = useRef(null);
  const [switchAccountOpen, setSwitchAccountOpen] = useState(false);
  const [switchAccountList, setSwitchAccountList] = useState([]);
  const [loadingSwitchList, setLoadingSwitchList] = useState(false);

  // Get primary color from theme
  const primaryColor = '#011C40'; // From theme.js

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load children/employees for switch account
  useEffect(() => {
    if (originalUser && (originalUser.roles.includes("PARENT") || originalUser.roles.includes("BUSINESS"))) {
      if (switchAccountOpen) {
        setLoadingSwitchList(true);
        (originalUser.roles.includes("PARENT") ? getMyChildrenAPI() : getMyEmployeesAPI())
          .then((res) => {
            const list = res.data.children || res.data.employees || [];
            setSwitchAccountList(list.filter(item => item.accountStatus !== "FROZEN" && item.accountStatus !== "DELETED"));
          })
          .catch(() => {})
          .finally(() => setLoadingSwitchList(false));
      }
    }
  }, [switchAccountOpen, originalUser]);

  const handleSwitchAccount = (child) => {
    dispatch(switchAccount({
      studentId: child.id || child._id,
      studentData: child
    }));
    setSwitchAccountOpen(false);
    navigate('/'); // Navigate to home after switching
  };

  const handleExitSwitchAccount = () => {
    dispatch(exitSwitchAccount());
    navigate('/'); // Navigate to home after exiting
  };

  useEffect(() => {
    if (effectiveUser) {
      if (effectiveUser.roles.includes("ADMIN")) {
        setMainLinks([
          { name: t('navbar.home'), path: '/' },
          { name: t('navbar.dashboard'), path: '/dashboard' },
        ]);
      } else if (effectiveUser.roles.includes("TUTOR")) {
        setMainLinks([
          { name: t('navbar.requests'), path: '/request' },
          { name: t('navbar.classRoom'), path: '/tutor-class-room' },
          { name: t('navbar.quizzes'), path: '/quiz/my' },
          { name: t('navbar.flashcards'), path: '/flashcard/my' },
        ]);
      } else if (effectiveUser.roles.includes("BUSINESS")) {
        // If in switch mode, show student links; otherwise show business links without classroom/quizzes/flashcards
        if (switchedAccount) {
          const links = [
            { name: t('navbar.home'), path: '/' },
            { name: t('navbar.requests'), path: '/student-request' },
            { name: t('navbar.classRoom'), path: '/student-class-room' },
            { name: t('navbar.quizzes'), path: '/quiz/student' },
            { name: t('navbar.flashcards'), path: '/flashcard/student' },
            { name: t('navbar.progress'), path: '/progress' },
          ];
          setMainLinks(links);
        } else {
          const links = [
            { name: t('navbar.home'), path: '/' },
            { name: t('navbar.requests'), path: '/student-request' },
            { name: t('navbar.progress'), path: '/progress' },
          ];
          setMainLinks(links);
        }
      } else if (effectiveUser.roles.includes("PARENT")) {
        // If in switch mode, show student links; otherwise show parent links without classroom/quizzes/flashcards
        if (switchedAccount) {
          const links = [
            { name: t('navbar.home'), path: '/' },
            { name: t('navbar.requests'), path: '/student-request' },
            { name: t('navbar.classRoom'), path: '/student-class-room' },
            { name: t('navbar.quizzes'), path: '/quiz/student' },
            { name: t('navbar.flashcards'), path: '/flashcard/student' },
            { name: t('navbar.progress'), path: '/progress' },
          ];
          setMainLinks(links);
        } else {
          const links = [
            { name: t('navbar.home'), path: '/' },
            { name: t('navbar.requests'), path: '/student-request' },
            { name: t('navbar.progress'), path: '/progress' },
          ];
          setMainLinks(links);
        }
      } else {
        // STUDENT role
        const links = [
          { name: t('navbar.home'), path: '/' },
          { name: t('navbar.requests'), path: '/student-request' },
          { name: t('navbar.classRoom'), path: '/student-class-room' },
          { name: t('navbar.quizzes'), path: '/quiz/student' },
          { name: t('navbar.flashcards'), path: '/flashcard/student' },
          { name: t('navbar.progress'), path: '/progress' },
        ];
        setMainLinks(links);
      }
    } else {
      setMainLinks([
        {
          name: t('navbar.home'),
          path: '/',
          type: 'dropdown',
          items: [
            { label: t('navbar.whyChooseUs'), path: '/#why-choose-us' },
            { label: t('navbar.aboutUs'), path: '/#about-us' },
            { label: t('navbar.howItWorks'), path: '/#how-it-works' },
          ]
        },
        {
          name: t('navbar.tutors'),
          path: '/tutors',
          type: 'dropdown',
          items: [
            { label: t('navbar.benefits'), path: '/tutors#benefits' },
            { label: t('navbar.howItWorks'), path: '/tutors#how-it-works' },
            { label: t('navbar.getStarted'), path: '/signup?role=Tutor' },
          ]
        },
        {
          name: t('navbar.students'),
          path: '/students',
          type: 'dropdown',
          items: [
            { label: t('navbar.benefits'), path: '/students#benefits' },
            { label: t('navbar.howItWorks'), path: '/students#how-it-works' },
            { label: t('navbar.getStarted'), path: '/signup?role=Student' },
          ]
        },
        {
          name: t('navbar.parents'),
          path: '/parents',
          type: 'dropdown',
          items: [
            { label: t('navbar.benefits'), path: '/parents#benefits' },
            { label: t('navbar.howItWorks'), path: '/parents#how-it-works' },
            { label: t('navbar.getStarted'), path: '/signup?role=Parent' },
          ]
        },
        {
          name: t('navbar.business'),
          path: '/business',
          type: 'dropdown',
          items: [
            { label: t('navbar.benefits'), path: '/business#benefits' },
            { label: t('navbar.howItWorks'), path: '/business#how-it-works' },
            { label: t('navbar.getStarted'), path: '/signup/business' },
          ]
        }
      ]);
    }
  }, [effectiveUser, switchedAccount, originalUser, t]);

  const handleProfileClick = () => {
    setProfileMenuOpen(false);
    navigate('/profile');
  };

  const handleTutorProfileClick = () => {
    setProfileMenuOpen(false);
    if (effectiveUser && effectiveUser.roles.includes('TUTOR')) {
      dispatch(getTutorByUser(effectiveUser.id));
      navigate('/tutor-profile');
      dispatch(clearTutor());
    }
  };

  const handleChangePasswordClick = () => {
    setProfileMenuOpen(false);
    navigate('/profile', { state: { openPasswordDialog: true } });
  };

  const handleLogoutClick = () => {
    setProfileMenuOpen(false);
    dispatch(clearUserState());
    dispatch(clearTutor());
    dispatch(logout());
    navigate('/login');
  };

  return (
    <>
      <CssBaseline />
      <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${switchedAccount ? 'with-switch-banner' : ''}`} style={{ '--primary-color': primaryColor }}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo">
            <img src="/white_text-modified.png" alt="logo" className="navbar-logo-img" />
            <span className="navbar-logo-text">Astegne</span>
          </Link>

          <div className="navbar-content">
            <div className="navbar-links">
              {mainLinks.map(link => {
                if (link.type === 'dropdown' && link.items) {
                  return (
                    <NavDropdown
                      key={link.path}
                      label={link.name}
                      path={link.path}
                      items={link.items}
                      primaryColor={primaryColor}
                    />
                  );
                }
                return (
                  <NavLink key={link.path} to={link.path} primaryColor={primaryColor}>
                    {link.name}
                  </NavLink>
                );
              })}
            </div>

            {!originalUser && (
              <div className="navbar-actions">
                <Link to="/login" className="custom-button custom-button-outlined">
                  {t('navbar.login')}
                </Link>
                <Link to="/signup" className="custom-button custom-button-contained">
                  {t('signup.signUp')}
                </Link>
              </div>
            )}

            {originalUser && (
              <div className="navbar-user" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Switch Account Button - Only show for PARENT or BUSINESS when not in switch mode */}
                {!switchedAccount && (originalUser.roles.includes("PARENT") || originalUser.roles.includes("BUSINESS")) && (
                  <button
                    className="navbar-user-button"
                    onClick={() => setSwitchAccountOpen(true)}
                    style={{ 
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px'
                    }}
                    title={t('navbar.switchAccount') || 'Switch Account'}
                  >
                    <SwapHoriz style={{ fontSize: '24px' }} />
                  </button>
                )}
                <button
                  ref={profileButtonRef}
                  className="navbar-user-button"
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                >
                  <CustomAvatar>{effectiveUser?.firstName?.charAt(0)?.toUpperCase() || originalUser.firstName?.charAt(0)?.toUpperCase()}</CustomAvatar>
                  <span>{effectiveUser?.firstName || originalUser.firstName}</span>
                </button>
                <CustomMenu
                  isOpen={profileMenuOpen}
                  onClose={() => setProfileMenuOpen(false)}
                  anchorRef={profileButtonRef}
                  primaryColor={primaryColor}
                >
                  <CustomMenuItem onClick={handleProfileClick} icon="👤">
                    {t('profile.personalInformation')}
                  </CustomMenuItem>
                  {effectiveUser?.roles.includes('TUTOR') && (
                    <CustomMenuItem onClick={handleTutorProfileClick} icon="⚙️">
                      {t('profile.tutorInformation')}
                    </CustomMenuItem>
                  )}
                  {originalUser.roles.includes('PARENT') && (
                    <CustomMenuItem onClick={() => { setProfileMenuOpen(false); navigate('/children'); }} icon="👨‍👩‍👧‍👦">
                      {t('navbar.children') || 'Children'}
                    </CustomMenuItem>
                  )}
                  {originalUser.roles.includes('BUSINESS') && (
                    <CustomMenuItem onClick={() => { setProfileMenuOpen(false); navigate('/employees'); }} icon="🏢">
                      {t('navbar.employees') || 'Employees'}
                    </CustomMenuItem>
                  )}
                  {switchedAccount && (
                    <>
                      <div className="custom-menu-divider" />
                      <CustomMenuItem onClick={() => { setProfileMenuOpen(false); handleExitSwitchAccount(); }} icon="↩️">
                        {t('navbar.exitSwitchMode') || 'Exit Switch Mode'}
                      </CustomMenuItem>
                    </>
                  )}
                  <CustomMenuItem onClick={handleChangePasswordClick} icon="🔒">
                    {t('profile.changePasswordLabel')}
                  </CustomMenuItem>
                  <div className="custom-menu-divider" />
                  <CustomMenuItem onClick={handleLogoutClick} icon="🚪" danger>
                    {t('profile.logout')}
                  </CustomMenuItem>
                </CustomMenu>
              </div>
            )}

            <div className="navbar-language">
              <LanguageToggle />
            </div>

            <button
              className="navbar-mobile-toggle"
              onClick={() => setDrawerOpen(true)}
              aria-label="menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      <CustomDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="drawer-header">
          <span className="drawer-title">Astegne</span>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="drawer-content">
          {mainLinks.map(link => {
            if (link.type === 'dropdown' && link.items) {
              return (
                <DrawerDropdownItem
                  key={link.path}
                  link={link}
                  onClose={() => setDrawerOpen(false)}
                />
              );
            }
            return (
              <Link
                key={link.path}
                to={link.path}
                className="drawer-link"
                onClick={() => setDrawerOpen(false)}
              >
                {link.name}
              </Link>
            );
          })}
          {!originalUser && (
            <>
              <div className="drawer-divider" />
              <Link
                to="/login"
                className="drawer-button drawer-button-outlined"
                onClick={() => setDrawerOpen(false)}
              >
                {t('navbar.login')}
              </Link>
              <Link
                to="/signup"
                className="drawer-button drawer-button-contained"
                onClick={() => setDrawerOpen(false)}
              >
                {t('signup.signUp')}
              </Link>
            </>
          )}
          {originalUser && (
            <>
              <div className="drawer-divider" />
              <Link
                to="/profile"
                className="drawer-link"
                onClick={() => setDrawerOpen(false)}
              >
                👤 {t('profile.personalInformation')}
              </Link>
              {effectiveUser?.roles.includes('TUTOR') && (
                <button
                  className="drawer-link"
                  onClick={() => {
                    setDrawerOpen(false);
                    handleTutorProfileClick();
                  }}
                >
                  ⚙️ {t('profile.tutorInformation')}
                </button>
              )}
              <button
                className="drawer-link"
                onClick={() => {
                  setDrawerOpen(false);
                  handleChangePasswordClick();
                }}
              >
                🔒 {t('profile.changePasswordLabel')}
              </button>
              <div className="drawer-divider" />
              <button
                className="drawer-link drawer-link-danger"
                onClick={() => {
                  setDrawerOpen(false);
                  handleLogoutClick();
                }}
              >
                🚪 {t('profile.logout')}
              </button>
            </>
          )}
        </div>
        <div className="drawer-footer">
          <LanguageToggle />
        </div>
      </CustomDrawer>
      <div className={`navbar-spacer ${switchedAccount ? 'with-switch-banner' : ''}`} />
      
      {/* Switch Account Modal */}
      <Dialog 
        open={switchAccountOpen} 
        onClose={() => setSwitchAccountOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>{originalUser?.roles.includes("PARENT") ? (t('navbar.switchToChild') || 'Switch to Child Account') : (t('navbar.switchToEmployee') || 'Switch to Employee Account')}</span>
            <IconButton onClick={() => setSwitchAccountOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingSwitchList ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : switchAccountList.length === 0 ? (
            <Typography>{originalUser?.roles.includes("PARENT") ? (t('navbar.noChildren') || 'No children available') : (t('navbar.noEmployees') || 'No employees available')}</Typography>
          ) : (
            <List>
              {switchAccountList.map((item) => {
                const name = item.userId ? `${item.userId.firstName || ''} ${item.userId.lastName || ''}`.trim() : (item.email || 'Unknown');
                return (
                  <ListItem key={item.id || item._id} disablePadding>
                    <ListItemButton onClick={() => handleSwitchAccount(item)}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {name.charAt(0).toUpperCase()}
                      </Avatar>
                      <ListItemText primary={name} secondary={item.userId?.email || item.email} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchAccountOpen(false)}>{t('common.cancel') || 'Cancel'}</Button>
        </DialogActions>
      </Dialog>

      {/* Switch Mode Banner */}
      {switchedAccount && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHoriz />
            <Typography variant="body2">
              {t('navbar.switchedTo') || 'Switched to'}: {switchedAccount.studentData?.userId ? `${switchedAccount.studentData.userId.firstName || ''} ${switchedAccount.studentData.userId.lastName || ''}`.trim() : 'Student Account'}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={handleExitSwitchAccount}
            sx={{ 
              color: 'inherit', 
              borderColor: 'currentColor',
              '&:hover': { borderColor: 'currentColor', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            {t('navbar.exitSwitchMode') || 'Exit Switch Mode'}
          </Button>
        </Box>
      )}
    </>
  );
}
